import { useEffect, useRef, useState } from 'react';

/**
 * ### useActiveProofStat
 *
 * Which of the scrolled entries is currently in the middle of the viewport.
 * A thin band across the viewport's centre is watched with an
 * IntersectionObserver; the entry crossing it is the active one, and the
 * sticky counter on the left follows it. Only the band is observed, not the
 * scroll position, so nothing runs on every scroll event.
 *
 * @param count Number of entries, so the observer can re-bind when it changes
 * @returns The active index and a callback ref to attach to each entry
 */
export function useActiveProofStat(count: number) {
  const [activeIndex, setActiveIndex] = useState(0);
  const elements = useRef<Array<HTMLElement | null>>([]);

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const index = elements.current.indexOf(entry.target as HTMLElement);
          if (index !== -1) setActiveIndex(index);
        }
      },
      // The band is the middle tenth of the viewport
      { rootMargin: '-45% 0px -45% 0px', threshold: 0 },
    );

    elements.current.slice(0, count).forEach((element) => {
      if (element) observer.observe(element);
    });

    return () => observer.disconnect();
  }, [count]);

  const setElementAt = (index: number) => (element: HTMLElement | null) => {
    elements.current[index] = element;
  };

  return { activeIndex, setElementAt };
}
